/**
 * ActionableTaskQueue - Enhanced Admin Dashboard Task Queue
 * 
 * Shows specific actionable items with direct action buttons per CQC requirement:
 * - Pending Verifications with [Verify] button
 * - References to Send with [Send Request] button
 * - Reference Responses to Review with [Review] button
 * - Forms Pending Review with [Review] button
 * - Induction Incomplete with [View] button
 * - Expiring Soon with [Send Reminder] button
 * - Workers Stuck in Onboarding with [Send Reminder] button
 */

import { useState, useEffect } from 'react';
import axios from 'axios';
import useWorkspaceNavigate from '../../hooks/useWorkspaceNavigate';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { 
  Loader2, FileCheck, Mail, MessageSquare, Clock, Users, 
  ChevronRight, RefreshCw, CheckCircle, Send, Eye, AlertTriangle,
  ClipboardList, FileText, GraduationCap, UserCheck, Filter
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import { useAuth } from '../../context/AuthContext';
import API_BASE from '../../utils/apiBase';
import {
  TASK_QUEUE_PAGE_SIZE,
  newestVerificationFirst,
  remainingTaskRows,
  visibleTaskRows,
  needsEvidenceHistoryReview,
  canRequestReplacementFromQueue,
  isCompleteTaskQueuePayload,
} from '../../lib/taskQueueVisibility';

const API = API_BASE;

const TASK_SCOPE_UI = {
  applicant: {
    title: 'APPLICANT TASKS',
    emptyTitle: 'No applicant task rows',
    description: 'Applicants only. Onboarding and active staff are kept in their own People views.',
    home: '/portal/recruitment',
  },
  onboarding: {
    title: 'ONBOARDING TASKS',
    emptyTitle: 'No onboarding task rows',
    description: 'Approved starters only. Active staff work stays under Employees.',
    home: '/portal/employees?stage_preset=ONBOARDING',
  },
  active: {
    title: 'EMPLOYEE TASKS',
    emptyTitle: 'No active employee task rows',
    description: 'Active staff only. Applicant and onboarding work is separated.',
    home: '/portal/employees',
  },
  recruitment: {
    title: 'RECRUITMENT TASKS',
    emptyTitle: 'No recruitment task rows',
    description: 'Legacy recruitment scope: applicants plus onboarding starters; active staff are excluded.',
    home: '/portal/recruitment',
  },
  employee: {
    title: 'EMPLOYEE TASKS TODAY',
    emptyTitle: 'No direct employee task rows',
    description: 'Employee-stage tasks across onboarding, active and inactive staff.',
    home: '/portal/employees',
  },
  all: {
    title: 'PEOPLE TASKS',
    emptyTitle: 'No direct people task rows',
    description: 'All supported lifecycle stages.',
    home: '/portal/recruitment',
  },
};

export default function ActionableTaskQueue({ compact = false, scope = 'employee', quietWhenEmpty = false }) {
  const { token } = useAuth();
  const navigate = useWorkspaceNavigate();
  const [tasks, setTasks] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');
  const [visibleCountBySection, setVisibleCountBySection] = useState({});
  const scopeUi = TASK_SCOPE_UI[scope] || TASK_SCOPE_UI.employee;
  const waitingHeading = scope === 'applicant'
    ? 'WAITING ON APPLICANT'
    : ['employee', 'active', 'onboarding'].includes(scope)
      ? 'WAITING ON STAFF MEMBER'
      : 'WAITING ON PERSON';

  const fetchTasks = async () => {
    try {
      const response = await axios.get(`${API}/admin/task-queue`, {
        params: { scope },
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!isCompleteTaskQueuePayload(response.data)) {
        throw new Error('Incomplete task-queue data; refusing to show a false all-clear');
      }
      setTasks(response.data);
      setFetchError(false);
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
      setTasks(null);
      setFetchError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchTasks();
      const interval = setInterval(fetchTasks, 5 * 60 * 1000);
      return () => clearInterval(interval);
    }
  }, [token, scope]);

  const handleSendReminder = async (employeeId, type) => {
    setActionLoading(`reminder-${employeeId}`);
    try {
      await axios.post(
        `${API}/workers/${employeeId}/send-reminder`,
        { reminder_type: type },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Reminder sent successfully');
      fetchTasks();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send reminder');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSendReferenceRequest = async (employeeId, refNum) => {
    setActionLoading(`ref-${employeeId}-${refNum}`);
    try {
      await axios.post(
        `${API}/references/${employeeId}/${refNum}/send-request`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Reference ${refNum} request sent`);
      fetchTasks();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send reference request');
    } finally {
      setActionLoading(null);
    }
  };

  const isCvTask = (task) => {
    const label = [
      task?.requirement_id,
      task?.document_type,
      task?.document_type_name,
      task?.category,
      task?.action,
    ].filter(Boolean).join(' ').toLowerCase();
    return label.includes('cv') || label.includes('resume') || label.includes('curriculum');
  };

  const buildDocumentQuery = (task) => {
    const params = new URLSearchParams();
    const cvTask = isCvTask(task);
    params.set('tab', task.target_tab || (cvTask ? 'employment' : 'checklist'));
    params.set('action', task.action || (cvTask ? 'review_cv' : 'review_document'));
    if (task.requirement_id || task.document_type) {
      params.set('requirement', task.requirement_id || task.document_type);
    }
    if (task.document_id || task.id) {
      params.set('document_id', task.document_id || task.id);
    }
    if (task.target_section) {
      params.set('section', task.target_section);
    }
    return params.toString();
  };

  const buildTaskQuery = (task, fallbackTab) => {
    const params = new URLSearchParams();
    params.set('tab', task.target_tab || fallbackTab);
    if (task.target_section) {
      params.set('section', task.target_section);
    }
    if (task.action) {
      params.set('action', task.action);
    }
    return params.toString();
  };

  const getTaskTitle = (task) => (
    task?.task_label
    || task?.document_type?.replace(/_/g, ' ')
    || task?.requirement_id?.replace(/_/g, ' ')
    || 'Task needs review'
  );

  const getTaskActionLabel = (task) => {
    if (task?.action_label) {
      return task.action_label;
    }
    if (task?.action === 'review_training' || task?.target_tab === 'training') {
      return 'Open Training & Evidence';
    }
    if (isCvTask(task)) {
      return 'Open CV Review';
    }
    if (task?.type === 'verification') {
      return 'Review Document';
    }
    if (task?.type === 'reference') {
      return 'Review References';
    }
    return 'Open Profile Review';
  };

  const requestDocumentReplacement = async (task) => {
    const documentId = task?.document_id || task?.id;
    if (!canRequestReplacementFromQueue(task) || !documentId) {
      toast.error('Open the document record before requesting a replacement.');
      return;
    }
    // This is a real worker notification and status change, not a silent
    // background cleanup. The manager must explicitly authorise it.
    if (!window.confirm(
      `Request a new copy of ${getTaskTitle(task)} for ${task.employee_name || 'this applicant'}? ` +
      'Only continue if you have checked that the original file is missing, unreadable or outdated. ' +
      'The existing document remains in the audit history and the applicant will be notified.'
    )) return;
    setActionLoading(`replacement-${documentId}`);
    try {
      await axios.post(
        `${API}/employee-documents/${documentId}/request-replacement`,
        {
          reason: 'The previous document needs to be replaced because the evidence could not be reviewed or is out of date. Please upload a current copy.',
          notify_employee: true,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('Replacement requested. Applicant notified.');
      await fetchTasks();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not request replacement. Open the record and check manually.');
    } finally {
      setActionLoading(null);
    }
  };

  const openTaskDetail = (task) => {
    if (!task?.employee_id) {
      navigate(scopeUi.home);
      return;
    }
    const isApplicant = task.person_stage === 'applicant';
    const employeeProfileBase = isApplicant
      ? `/portal/recruitment/${task.employee_id}`
      : `/portal/employees/${task.employee_id}`;

    switch (task.type) {
      case 'verification':
      case 'replacement':
      case 'expiring':
        navigate(`${employeeProfileBase}?${buildDocumentQuery(task)}`);
        return;
      case 'waiting_on_applicant':
        navigate(`${employeeProfileBase}?${buildTaskQuery(task, 'employment')}`);
        return;
      case 'reference':
        navigate(`${employeeProfileBase}?${buildTaskQuery(task, 'references')}`);
        return;
      case 'spot_check':
      case 'followup':
        navigate(`${employeeProfileBase}?${buildTaskQuery(task, 'spot_checks')}`);
        return;
      case 'competency_assessment':
        navigate(`${employeeProfileBase}?${buildTaskQuery(task, 'competencies')}`);
        return;
      default:
        navigate(employeeProfileBase);
    }
  };

  if (loading) {
    return (
      <Card className="border border-gray-200 shadow-sm">
        <CardContent className="py-8 flex justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  if (fetchError) {
    return (
      <Card className="border border-amber-200 bg-amber-50/40 shadow-sm" data-testid="task-queue-error">
        <CardContent className="py-8 text-center space-y-3">
          <AlertTriangle className="h-10 w-10 mx-auto text-amber-600" />
          <p className="font-medium text-amber-900">Could not load your task queue</p>
          <p className="text-sm text-amber-800">Tasks may still be pending — refresh to try again.</p>
          <Button variant="outline" size="sm" onClick={() => { setLoading(true); fetchTasks(); }}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  const allTasks = tasks?.tasks || [];
  const pendingVerifications = newestVerificationFirst(allTasks.filter(t => t.type === 'verification'));
  const waitingOnApplicant = tasks?.waiting_tasks || [];
  const missingEvidence = allTasks.filter(t => t.type === 'replacement');
  const pendingReferences    = allTasks.filter(t => t.type === 'reference');
  const expiringSoon         = allTasks.filter(t => t.type === 'expiring');
  const scheduledTasks       = allTasks.filter(t => t.type === 'spot_check' || t.type === 'competency_assessment');
  const overdueFollowups     = allTasks.filter(t => t.type === 'followup');

  // These categories have no backend data yet — sections stay hidden until backend provides them
  const inductionIncomplete = 0;
  const interviewsPending = 0;
  const stuckWorkers = [];

  // Category counts — 'all' uses backend canonical total so "All Tasks Complete" is never false-positive
  const categoryCounts = {
    all:          tasks?.total_tasks || 0,
    verification: pendingVerifications.length,
    waiting: waitingOnApplicant.length,
    replacement: missingEvidence.length,
    references:   pendingReferences.length,
    onboarding:   0,
    expiring:     expiringSoon.length,
    recurring:    scheduledTasks.length + overdueFollowups.length
  };

  if (categoryCounts.all === 0 && categoryCounts.waiting === 0 && !tasks?.partial_results) {
    if (quietWhenEmpty) {
      return null;
    }
    return (
      <Card className="border border-green-200 bg-green-50/30 shadow-sm">
        <CardContent className="py-8 text-center">
          <CheckCircle className="h-12 w-12 mx-auto mb-3 text-green-500" />
          <p className="font-medium text-green-700">{scopeUi.emptyTitle}</p>
          <p className="text-sm text-green-600 mt-1">{scopeUi.description}</p>
          <p className="text-xs text-green-600/80 mt-1">
            This queue only lists row-by-row reviews. Wider compliance risks still appear in readiness and renewal panels.
          </p>
        </CardContent>
      </Card>
    );
  }

  // Filter sections based on active tab
  const showSection = (category) => {
    if (activeFilter === 'all') return true;
    return activeFilter === category;
  };

  // Compact lists never strand the remaining rows behind a navigation link
  // back to this same recruitment page. Open them in-place, five at a time.
  const rowsFor = (category, entries) =>
    visibleTaskRows(entries, compact, visibleCountBySection[category] || TASK_QUEUE_PAGE_SIZE);

  const sectionControls = (category, entries) => {
    if (!compact || entries.length <= TASK_QUEUE_PAGE_SIZE) return null;
    const current = visibleCountBySection[category] || TASK_QUEUE_PAGE_SIZE;
    const remaining = remainingTaskRows(entries, true, current);
    return (
      <div className="flex flex-wrap items-center justify-center gap-3 border-t border-slate-100 px-4 py-3"
        data-testid={`queue-section-controls-${category}`}>
        {remaining > 0 && (
          <Button type="button" variant="outline" size="sm"
            onClick={() => setVisibleCountBySection(prev => ({
              ...prev,
              [category]: (prev[category] || TASK_QUEUE_PAGE_SIZE) + TASK_QUEUE_PAGE_SIZE,
            }))}
            data-testid={`queue-show-more-${category}`}>
            Show {Math.min(TASK_QUEUE_PAGE_SIZE, remaining)} more · {remaining} remaining
          </Button>
        )}
        {current > TASK_QUEUE_PAGE_SIZE && (
          <Button type="button" variant="ghost" size="sm"
            onClick={() => setVisibleCountBySection(prev => ({
              ...prev, [category]: TASK_QUEUE_PAGE_SIZE,
            }))}
            data-testid={`queue-show-fewer-${category}`}>Show fewer</Button>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4" data-testid="actionable-task-queue">
      {/* Header with Tabs */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            {scopeUi.title}
            <Badge className="bg-amber-100 text-amber-700 ml-2">{tasks?.partial_results ? `${categoryCounts.all}+` : categoryCounts.all}</Badge>
          </h2>
          <p className="mt-1 text-xs text-slate-500" data-testid="task-queue-scope-copy">
            {scopeUi.description}
          </p>
          {categoryCounts.waiting > 0 && (
            <p className="mt-1 text-xs text-slate-600" data-testid="waiting-summary">
              {categoryCounts.waiting} waiting on the person · not counted as admin actions.
            </p>
          )}
          {tasks?.generated_at && !Number.isNaN(Date.parse(tasks.generated_at)) && (
            <p className="mt-1 text-xs text-slate-500" data-testid="task-queue-as-of">
              Tasks checked {new Date(tasks.generated_at).toLocaleString()}. Refresh after changing a worker's check.
            </p>
          )}
          {tasks?.partial_results && (
            <p role="alert" data-testid="task-queue-incomplete" className="mt-2 max-w-xl rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-950">
              This task list may be incomplete because it reached a safe loading limit.
              The displayed total is only a minimum; do not treat it as full compliance clearance.
              Check the individual employee records while the complete list is repaired.
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Tabs value={activeFilter} onValueChange={setActiveFilter} className="w-auto">
            <TabsList className="h-8 p-1 bg-gray-100">
              <TabsTrigger value="all" className="text-xs px-2 py-1 h-6">
                All ({categoryCounts.all})
              </TabsTrigger>
              <TabsTrigger value="verification" className="text-xs px-2 py-1 h-6">
                <FileCheck className="h-3 w-3 mr-1" />
                Verify ({categoryCounts.verification})
              </TabsTrigger>
              {categoryCounts.waiting > 0 && (
                <TabsTrigger value="waiting" className="text-xs px-2 py-1 h-6" data-testid="queue-waiting-filter">
                  <Clock className="h-3 w-3 mr-1" />
                  Waiting ({categoryCounts.waiting})
                </TabsTrigger>
              )}
              {categoryCounts.replacement > 0 && (
                <TabsTrigger value="replacement" className="text-xs px-2 py-1 h-6" data-testid="queue-replacement-filter">
                  <AlertTriangle className="h-3 w-3 mr-1" />
                  Replace ({categoryCounts.replacement})
                </TabsTrigger>
              )}
              <TabsTrigger value="references" className="text-xs px-2 py-1 h-6">
                <Mail className="h-3 w-3 mr-1" />
                Refs ({categoryCounts.references})
              </TabsTrigger>
              {categoryCounts.onboarding > 0 && (
                <TabsTrigger value="onboarding" className="text-xs px-2 py-1 h-6">
                  <Users className="h-3 w-3 mr-1" />
                  Onboarding ({categoryCounts.onboarding})
                </TabsTrigger>
              )}
              {categoryCounts.expiring > 0 && (
                <TabsTrigger value="expiring" className="text-xs px-2 py-1 h-6">
                  <Clock className="h-3 w-3 mr-1" />
                  Expiring ({categoryCounts.expiring})
                </TabsTrigger>
              )}
            </TabsList>
          </Tabs>
          <Button variant="ghost" size="sm" onClick={fetchTasks} className="h-8 w-8 p-0">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Pending Verifications */}
      {showSection('verification') && pendingVerifications.length > 0 && (
        <Card className="border border-blue-200 shadow-sm overflow-hidden">
          <CardHeader className="py-3 px-4 bg-gradient-to-r from-blue-50 to-blue-100/50">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <FileCheck className="h-4 w-4 text-blue-600" />
                PENDING VERIFICATIONS
                <Badge className="bg-blue-600 text-white ml-1">{tasks?.summary?.pending_verifications ?? pendingVerifications.length}</Badge>
              </CardTitle>
              {compact && pendingVerifications.length > TASK_QUEUE_PAGE_SIZE && (
                <span className="text-xs text-blue-700">Newest uploads first</span>
              )}
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-gray-100">
              {rowsFor('verification', pendingVerifications).map((doc, idx) => (
                <div key={idx} className="flex items-center justify-between px-4 py-3 hover:bg-blue-50/30 transition-colors">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-800 truncate">{getTaskTitle(doc)}</span>
                      <Badge variant="outline" className="text-xs shrink-0">
                        {doc.employee_name?.split(' ')[0]}
                      </Badge>
                    </div>
                    {doc.task_detail && (
                      <p className="text-xs text-gray-500 mt-0.5">{doc.task_detail}</p>
                    )}
                    {doc.uploaded_at && (
                      <span className="text-xs text-gray-400">
                        Uploaded {new Date(doc.uploaded_at).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                  <div className="flex gap-2 shrink-0 ml-2">
                    <Button type="button" size="sm" onClick={() => openTaskDetail(doc)}
                      className="bg-blue-600 hover:bg-blue-700 min-h-11">
                      <Eye className="h-3.5 w-3.5 mr-1" />
                      {getTaskActionLabel(doc)}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            {sectionControls('verification', pendingVerifications)}
          </CardContent>
        </Card>
      )}

      {/* An applicant-owned dependency is visible but does not inflate admin Verify. */}
      {showSection('waiting') && waitingOnApplicant.length > 0 && (
        <Card className="border border-slate-200 shadow-sm overflow-hidden" data-testid="waiting-on-applicant-tasks">
          <CardHeader className="py-3 px-4 bg-slate-50">
            <CardTitle className="text-sm font-semibold text-slate-800 flex items-center gap-2">
              <Clock className="h-4 w-4" />
              {waitingHeading}
              <Badge variant="outline">{waitingOnApplicant.length}</Badge>
            </CardTitle>
            <p className="text-xs text-slate-600">
              Not a new admin verification. The original CV, evidence and incomplete
              employment-history requirement remain on file until the applicant responds.
            </p>
          </CardHeader>
          <CardContent className="p-0 divide-y divide-slate-100">
            {rowsFor('waiting', waitingOnApplicant).map(task => (
              <div key={task.id} className="flex items-center justify-between gap-3 px-4 py-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-900">
                    {task.task_label} <span className="text-slate-500">· {task.employee_name}</span>
                  </p>
                  <p className="text-xs text-slate-600 mt-1">{task.task_detail}</p>
                </div>
                <Button type="button" variant="outline" size="sm" className="shrink-0 min-h-11"
                  onClick={() => openTaskDetail(task)}>
                  <Eye className="h-3.5 w-3.5 mr-1" />
                  {task.action_label || 'View status'}
                </Button>
              </div>
            ))}
            {sectionControls('waiting', waitingOnApplicant)}
          </CardContent>
        </Card>
      )}

      {/* Missing file references are renewal/replacement work, never verification. */}
      {showSection('replacement') && missingEvidence.length > 0 && (
        <Card className="border border-amber-200 shadow-sm overflow-hidden" data-testid="missing-evidence-replacements">
          <CardHeader className="py-3 px-4 bg-amber-50">
            <CardTitle className="text-sm font-semibold text-amber-900 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              EVIDENCE TO RECOVER / REPLACE
              <Badge className="bg-amber-700 text-white">{missingEvidence.length}</Badge>
            </CardTitle>
            <p className="text-xs text-amber-900">Some saved records have no linked file; others have a link known to be unavailable. Review each record for recovery or request a replacement. No previous evidence is deleted or approved.</p>
          </CardHeader>
          <CardContent className="p-0 divide-y divide-amber-100">
            {rowsFor('replacement', missingEvidence).map(doc => (
              <div key={doc.document_id || doc.id} className="flex items-center justify-between gap-3 px-4 py-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-900">{getTaskTitle(doc)} <span className="text-slate-500">· {doc.employee_name}</span></p>
                  <p className="text-xs text-slate-600 mt-1">{doc.task_detail}</p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button type="button" size="sm" variant="outline" onClick={() => openTaskDetail(doc)} className="min-h-11"
                    data-testid={needsEvidenceHistoryReview(doc) ? 'review-evidence-history' : 'view-missing-evidence'}>
                    {needsEvidenceHistoryReview(doc) ? 'Review history' : 'View'}
                  </Button>
                  {canRequestReplacementFromQueue(doc) && (
                    <Button type="button" size="sm" onClick={() => requestDocumentReplacement(doc)}
                      disabled={Boolean(actionLoading)} className="min-h-11 bg-amber-800 hover:bg-amber-900"
                      data-testid="request-missing-evidence">
                      {actionLoading === `replacement-${doc.document_id || doc.id}` ? 'Sending…' : 'Request new copy'}
                    </Button>
                  )}
                </div>
              </div>
            ))}
            {sectionControls('replacement', missingEvidence)}
          </CardContent>
        </Card>
      )}

      {/* Pending References */}
      {showSection('references') && pendingReferences.length > 0 && (
        <Card className="border border-purple-200 shadow-sm overflow-hidden">
          <CardHeader className="py-3 px-4 bg-gradient-to-r from-purple-50 to-purple-100/50">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Mail className="h-4 w-4 text-purple-600" />
                PENDING REFERENCES
                <Badge className="bg-purple-600 text-white ml-1">{pendingReferences.length}</Badge>
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-gray-100">
              {rowsFor('references', pendingReferences).map((ref, idx) => (
                <div key={idx} className="flex items-center justify-between px-4 py-3 hover:bg-purple-50/30 transition-colors">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-800">Reference {ref.reference_num}</span>
                      <span className="text-gray-400">•</span>
                      <span className="text-gray-600 truncate">{ref.employee_name}</span>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => openTaskDetail(ref)}
                    className="bg-purple-600 hover:bg-purple-700 shrink-0 ml-2"
                  >
                    <Eye className="h-3.5 w-3.5 mr-1" />
                    Open References
                  </Button>
                </div>
              ))}
            </div>
            {sectionControls('references', pendingReferences)}
          </CardContent>
        </Card>
      )}

      {/* Induction Checklists Incomplete */}
      {showSection('onboarding') && inductionIncomplete > 0 && (
        <Card className="border border-orange-200 shadow-sm overflow-hidden">
          <CardHeader className="py-3 px-4 bg-gradient-to-r from-orange-50 to-orange-100/50">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <ClipboardList className="h-4 w-4 text-orange-600" />
                INDUCTION CHECKLISTS INCOMPLETE
                <Badge className="bg-orange-600 text-white ml-1">{inductionIncomplete}</Badge>
              </CardTitle>
              <Button 
                variant="link" 
                size="sm" 
                className="text-orange-600 p-0 h-auto"
                onClick={() => navigate(scopeUi.home)}
              >
                View All <ChevronRight className="h-3 w-3 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-3">
            <p className="text-sm text-gray-600">
              {inductionIncomplete} worker{inductionIncomplete !== 1 ? 's' : ''} have incomplete induction checklists.
              Complete these to ensure Care Certificate compliance.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Interviews/Approvals Pending */}
      {showSection('onboarding') && interviewsPending > 0 && (
        <Card className="border border-indigo-200 shadow-sm overflow-hidden">
          <CardHeader className="py-3 px-4 bg-gradient-to-r from-indigo-50 to-indigo-100/50">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <UserCheck className="h-4 w-4 text-indigo-600" />
                INTERVIEWS/APPROVALS PENDING
                <Badge className="bg-indigo-600 text-white ml-1">{interviewsPending}</Badge>
              </CardTitle>
              <Button 
                variant="link" 
                size="sm" 
                className="text-indigo-600 p-0 h-auto"
                onClick={() => navigate(scopeUi.home)}
              >
                View All <ChevronRight className="h-3 w-3 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-3">
            <p className="text-sm text-gray-600">
              {interviewsPending} applicant{interviewsPending !== 1 ? 's' : ''} awaiting interview or recruitment approval.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Expiring Soon */}
      {showSection('expiring') && expiringSoon.length > 0 && (
        <Card className="border border-amber-200 shadow-sm overflow-hidden">
          <CardHeader className="py-3 px-4 bg-gradient-to-r from-amber-50 to-amber-100/50">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Clock className="h-4 w-4 text-amber-600" />
                EXPIRING SOON
                <Badge className="bg-amber-600 text-white ml-1">{expiringSoon.length}</Badge>
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-gray-100">
              {expiringSoon.map((item, idx) => (
                <div key={idx} className="flex items-center justify-between px-4 py-3 hover:bg-amber-50/30 transition-colors">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-800 truncate">{(item.document_type || '').replace(/_/g, ' ')}</span>
                      <Badge variant="outline" className="text-xs shrink-0">
                        {item.employee_name?.split(' ')[0]}
                      </Badge>
                    </div>
                    <Badge className={cn(
                      "text-xs mt-1",
                      item.days_until <= 14 ? "bg-red-100 text-red-700" :
                      item.days_until <= 30 ? "bg-amber-100 text-amber-700" :
                      "bg-blue-100 text-blue-700"
                    )}>
                      {item.days_until} days left
                    </Badge>
                  </div>
                  <Button 
                    size="sm"
                    variant="outline"
                    onClick={() => handleSendReminder(item.employee_id, item.type)}
                    disabled={actionLoading === `reminder-${item.employee_id}`}
                    className="text-amber-600 border-amber-200 hover:bg-amber-50 shrink-0 ml-2"
                  >
                    {actionLoading === `reminder-${item.employee_id}` ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <>
                        <Send className="h-3.5 w-3.5 mr-1" />
                        Remind
                      </>
                    )}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openTaskDetail(item)}
                    className="shrink-0 ml-2"
                  >
                    <Eye className="h-3.5 w-3.5 mr-1" />
                    Open Renewal Source
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Scheduled Checks & Overdue Follow-ups */}
      {showSection('recurring') && (scheduledTasks.length > 0 || overdueFollowups.length > 0) && (
        <Card className="border border-cyan-200 shadow-sm overflow-hidden">
          <CardHeader className="py-3 px-4 bg-gradient-to-r from-cyan-50 to-cyan-100/50">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <GraduationCap className="h-4 w-4 text-cyan-600" />
                RECURRING COMPLIANCE DUE THIS WEEK
              </CardTitle>
              <Button
                variant="link"
                size="sm"
                className="text-cyan-600 p-0 h-auto"
                onClick={() => navigate('/portal/compliance-centre')}
              >
                View All <ChevronRight className="h-3 w-3 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-3">
            <div className="space-y-2">
              {scheduledTasks.map((task, idx) => (
                <div key={`${task.id || idx}-${task.type}`} className="flex items-center justify-between px-3 py-2 bg-white rounded-lg border">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">
                      {task.type === 'spot_check' ? 'Spot check due' : 'Competency assessment due'} • {task.employee_name || 'Employee'}
                    </p>
                    {task.scheduled_date && (
                      <p className="text-xs text-gray-500">Due {task.scheduled_date}</p>
                    )}
                  </div>
                  <Button size="sm" variant="outline" onClick={() => openTaskDetail(task)} className="ml-2 shrink-0">
                    <Eye className="h-3.5 w-3.5 mr-1" />
                    Open
                  </Button>
                </div>
              ))}
              {overdueFollowups.map((task, idx) => (
                <div key={`${task.id || idx}-${task.type}`} className="flex items-center justify-between px-3 py-2 bg-white rounded-lg border border-red-100 bg-red-50/30">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">
                      Overdue follow-up • {task.employee_name || 'Employee'}
                    </p>
                    {task.follow_up_date && (
                      <p className="text-xs text-gray-500">Was due {task.follow_up_date}</p>
                    )}
                  </div>
                  <Button size="sm" variant="outline" onClick={() => openTaskDetail(task)} className="ml-2 shrink-0">
                    <Eye className="h-3.5 w-3.5 mr-1" />
                    Open
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Workers Stuck in Onboarding */}
      {showSection('onboarding') && stuckWorkers.length > 0 && (
        <Card className="border border-slate-200 shadow-sm overflow-hidden">
          <CardHeader className="py-3 px-4 bg-gradient-to-r from-slate-50 to-slate-100/50">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Users className="h-4 w-4 text-slate-600" />
                WORKERS STUCK IN ONBOARDING
                <Badge className="bg-slate-600 text-white ml-1">{stuckWorkers.length}</Badge>
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-gray-100">
              {stuckWorkers.map((worker, idx) => (
                <div key={idx} className="flex items-center justify-between px-4 py-3 hover:bg-slate-50/30 transition-colors">
                  <div className="flex-1 flex items-center gap-3">
                    <span className="font-medium text-gray-800">{worker.employee_name}</span>
                    <Badge variant="outline" className={cn(
                      "text-xs",
                      worker.progress < 25 ? "border-red-200 text-red-600" :
                      worker.progress < 50 ? "border-amber-200 text-amber-600" :
                      "border-blue-200 text-blue-600"
                    )}>
                      {worker.progress}% complete
                    </Badge>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      size="sm"
                      variant="outline"
                      onClick={() => navigate(`/portal/employees/${worker.employee_id}`)}
                    >
                      <Eye className="h-3.5 w-3.5 mr-1" />
                      View
                    </Button>
                    <Button 
                      size="sm"
                      variant="outline"
                      onClick={() => handleSendReminder(worker.employee_id, 'general')}
                      disabled={actionLoading === `reminder-${worker.employee_id}`}
                    >
                      {actionLoading === `reminder-${worker.employee_id}` ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <>
                          <Send className="h-3.5 w-3.5 mr-1" />
                          Remind
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

